import React, { useMemo, useState } from "react";
import { Typography, Box, Select, MenuItem, Button } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getBillPaymentHistory,
  getProfileInfo,
  getAccountLookup,
  getTooltipMsgs,
} from "selectors/energyAssistance.selectors";
import { InfoTooltip } from "_components";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { getBillPaymentPDFApi } from "services/energyAssistance/energyAssistance.thunks";
import styles from "./index.module.css";
import { initialState } from "container/energyAssistance/store/initialState";
import { API_LOAD_STATUS, ERR_MSG } from "constants/labels";
import { alertActions } from "_store";

const PaymentTransactionHistory = () => {
  const header = "Billing & Payment History";
  const dispatch = useDispatch();

  const billPaymentHistory = useSelector(getBillPaymentHistory);
  const tooltipMsgs = useSelector(getTooltipMsgs);
  const getTooltip = (title) =>
    tooltipMsgs?.Data?.find((t) => t.TooltipTitle === title)?.TooltipMsg;
  const { Data, Load, Succeeded } =
    billPaymentHistory || initialState.billingAndPaymentHistory;
  const data = Data || initialState.billingAndPaymentHistory.Data;

  const billHistory = data?.BillingHistory_Res?.LegacyBillDates || [];
  const paymentHistory = data?.PaymentHistory_Res?.PaymentDetails_Res || [];
  const AccountNumber = data?.BillingHistory_Res?.AccountNumber || "";

  const sortedPayments = paymentHistory.slice().sort((a, b) => {
    const dateA = dayjs(a.PostDate);
    const dateB = dayjs(b.PostDate);
    return dateA.isAfter(dateB) ? -1 : 1;
  });

  const sortedBillHistory = billHistory.slice().sort((a, b) => {
    const dateA = dayjs(a.BillDate);
    const dateB = dayjs(b.BillDate);
    return dateA.isAfter(dateB) ? -1 : 1;
  });

  const [billDate, setBillDate] = useState(
    sortedBillHistory[0]?.BillDate || ""
  );
  const [isDisabled, setIsDisabled] = useState(true);

  const convertDate = (rawDate) => {
    return dayjs(rawDate).format("MM-DD-YYYY");
  };

  const convertDateSlash = (date) => {
    return dayjs(date).format("MM/DD/YYYY");
  };

  const convertWordedDate = (date) => {
    return dayjs(date).format("MMM DD, YYYY");
  };

  const convertCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const handleViewPDF = async () => {
    try {
      const selectedBill = sortedBillHistory[billDate];
      if (!selectedBill) return;
      const billingPDFData = await dispatch(
        getBillPaymentPDFApi({
          AccountNumber,
          DateRequested: convertDateSlash(selectedBill.BillDate),
        })
      ).unwrap();

      if (billingPDFData?.Data?.DocContent === "") {
        dispatch(
          alertActions.error({
            message: billingPDFData?.Data?.SapErrors[0]?.MessageTxt1,
            header: header,
          })
        );
        return;
      }

      if (billingPDFData) {
        const binary = atob(billingPDFData.Data.DocContent);
        const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
        const blob = new Blob([bytes], { type: "application/pdf" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // link.download = `BillPayment_${convertDate(billDate)}.pdf`;
        link.download = `BillPayment_${convertDate(selectedBill.BillDate)}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      dispatch(
        alertActions.error({
          message: error.message ? ERR_MSG["073"] : error,
          header: header,
        })
      );
    }
  };

  const tableCols = useMemo(
    () => [
      {
        accessorKey: "PostDate",
        header: "Transaction Date",
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          const formattedDate = convertDate(rawDate);
          return formattedDate;
        },
      },
      {
        accessorKey: "PaymentAmount",
        header: "Payment Amount",
        Cell: ({ cell }) => {
          const rawAmount = cell.getValue();
          const formattedAmount = convertCurrency(rawAmount);
          return formattedAmount;
        },
      },
      {
        accessorKey: "PortalDescription",
        header: "Payment Source",
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns: tableCols,
    data: sortedPayments,
    enableColumnActions: false,
    enableColumnFilters: false,
    enablePagination: false,
    enableSorting: false,
    muiTableBodyRowProps: { hover: false },
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    muiTableProps: {
      sx: {
        backgroundColor: "transparent",
        caption: {
          captionSide: "top",
        },
      },
    },
    muiTableHeadCellProps: {
      sx: {
        border: "1px solid #ccc",
        textAlign: "center !important",
        justifyContent: "center",
        fontWeight: "normal",
      },
    },
    muiTableBodyCellProps: {
      sx: {
        textAlign: "center !important",
        border: "1px solid #ccc",
        justifyContent: "center",
        backgroundColor: "transparent",
        fontWeight: "normal !important",
        color: "#495057",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        background: "transparent !important",
      },
    },
  });

  return (
    <Box
      className="userInformationcontainer"
      sx={{ height: "100% !important" }}
    >
      <Accordion component="div" defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          component="div"
        >
          <Typography component="h2" className="userInformation">
            Billing & Payment History{" "}
            <InfoTooltip
              title="Billing and Payment History"
              message={getTooltip("Billing and Payment History")}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {Load.status === API_LOAD_STATUS.FULFILLED && Succeeded && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                <Box
                  className={styles.billDate}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: 2,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body1"
                    component="div"
                    className={styles.billDateText}
                    sx={{
                      textAlign: { xs: "center", sm: "left", md: "left" },
                      display: { xs: "flex", sm: "block", md: "block" },
                      justifyContent: { xs: "center", md: "flex-start" },
                      width: { xs: "100%", sm: "fit-content" },
                    }}
                  >
                    Bill Date
                  </Typography>

                  <Select
                    labelId="bill-date"
                    displayEmpty
                    value={billDate}
                    onChange={(e) => {
                      setBillDate(e.target.value);
                      setIsDisabled(false);
                    }}
                    sx={{
                      width: { xs: "100vw", sm: "100px", md: "300px" },
                      flexGrow: 1,
                      height: 40,
                    }}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select an option
                    </MenuItem>
                    {sortedBillHistory.map((bill, index) => (
                      <MenuItem key={index} value={index}>
                        {`${convertWordedDate(bill.BillDate)} Period: ${
                          bill.BillStartDate
                        } - ${bill.BillEndDate} Amount Due: ${convertCurrency(
                          bill.InvoiceAmount
                        )}`}
                      </MenuItem>
                    ))}
                  </Select>

                  <Button
                    variant="contained"
                    className={styles.viewPDFButton}
                    onClick={handleViewPDF}
                    disabled={isDisabled}
                    sx={{
                      flexShrink: 0,
                      width: {
                        xs: "100%",
                        sm: "fit-content",
                        md: "fit-content",
                      },
                    }}
                  >
                    View Printable PDF
                  </Button>
                </Box>

                <Grid container spacing={3}>
                  <Grid
                    size={{ xs: 12, sm: 12, md: 12 }}
                    className="Billingcontainer"
                  >
                    <MaterialReactTable table={table} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {Load.status === API_LOAD_STATUS.PENDING && (
            <Typography component="p" sx={{ textAlign: "center" }}>
              Loading Billing &amp; Payment History...
            </Typography>
          )}
          {(Load.status === API_LOAD_STATUS.REJECTED ||
            Load.status === API_LOAD_STATUS.FULFILLED) &&
            !Succeeded && (
              <Typography component="p" sx={{ textAlign: "center" }}>
                {Load.message || "No records to display."}
              </Typography>
            )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PaymentTransactionHistory;
